<script setup lang="ts">
/**
 * SceneMeeting · 忙碌小剧场（变体）：白板前讲方案
 * ------------------------------------------------------------
 * 舞台 = 一块立着的小白板（柱状图 + 上升折线）：小家伙边蹦边讲，
 * 举教鞭往板上指指点点——忙碌不止是打字，还有讲不完的方案。
 * 人物比例与所有场景统一：头 r6.6、0.8px/viewBox 单位（见 scenes/index.ts）。
 */
defineProps<{ peek?: boolean; hot?: boolean }>()
</script>

<template>
  <span class="sc" :class="{ hot }" aria-hidden="true">
    <i class="floor" />
    <span class="bounce">
      <svg viewBox="0 0 34 30">
        <defs>
          <linearGradient id="apShirtMeet" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#7A95FF" />
            <stop offset="1" stop-color="#4C6BF0" />
          </linearGradient>
        </defs>
        <!-- 白板：板面 + 立柱 + 笔托 + 图表 -->
        <rect x="18.4" y="17" width="1.2" height="8" fill="#A9B4C6" />
        <rect x="29.6" y="17" width="1.2" height="8" fill="#A9B4C6" />
        <rect x="16" y="3.5" width="16.5" height="13.5" rx="1.2" fill="#F6FAFF" stroke="#C9D2DE" stroke-width=".6" />
        <rect x="17.2" y="16.6" width="14.1" height=".9" rx=".45" fill="#C9D2DE" />
        <rect x="19" y="12.5" width="2.2" height="3.5" rx=".4" fill="#7A95FF" />
        <rect x="22.2" y="10.5" width="2.2" height="5.5" rx=".4" fill="#4CC3F0" />
        <rect x="25.4" y="11.8" width="2.2" height="4.2" rx=".4" fill="#7FE0A6" />
        <path d="M19 11.5 L23.3 8.6 L27.6 9.8 L30.6 6.4" stroke="#4C6BF0" stroke-width=".8"
              fill="none" stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="30.6" cy="6.4" r=".8" fill="#4C6BF0" />
        <!-- 小家伙（边讲边轻轻蹦） -->
        <g>
          <animateTransform attributeName="transform" type="translate"
                            values="0 0;0 -.7;0 0" dur=".92s" repeatCount="indefinite" />
          <!-- 身体（连帽衫） -->
          <path d="M2.4 26v-6.4c0-2.9 2.4-4.6 5.6-4.6s5.6 1.7 5.6 4.6V26z" fill="url(#apShirtMeet)" />
          <path d="M5.3 16.2q2.7 2.1 5.4 0" stroke="#FFFFFF" stroke-opacity=".55" stroke-width=".9"
                fill="none" stroke-linecap="round" />
          <!-- 左手垂着 -->
          <path d="M4.8 17.4 L4.4 21.6" stroke="#4C6BF0" stroke-width="1.9" stroke-linecap="round" />
          <circle cx="4.4" cy="21.8" r=".95" fill="#FFDCB4" stroke="#E6B183" stroke-width=".5" />
          <!-- 右手举教鞭点白板（绕肩轻点） -->
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="-6 11.6 17.4;8 11.6 17.4;-6 11.6 17.4" dur="1.15s" repeatCount="indefinite" />
            <path d="M11.6 17.4 L15.6 13.4" stroke="#4C6BF0" stroke-width="1.9" stroke-linecap="round" />
            <circle cx="15.6" cy="13.4" r="1.05" fill="#FFDCB4" stroke="#E6B183" stroke-width=".5" />
            <path d="M15.6 13.4 L22.6 8.2" stroke="#B4794B" stroke-width=".7" stroke-linecap="round" />
          </g>
          <!-- 头 -->
          <g>
            <circle cx="8" cy="8.6" r="6.6" fill="#FFDCB4" stroke="#E6B183" stroke-width=".9" />
            <path d="M1.5 8.1a6.5 6.5 0 0 1 13 0v.8H1.5z" fill="#3A2B21" />
            <path d="M7.5 2.8c1.1-1.6 2.7-1.9 3.5-1.4-1.3.4-2.1 1-2.5 1.8z" fill="#3A2B21" />
            <path d="M4.9 6.1q1.1-.7 2.2-.2M11.1 6.1q-1.1-.7-2.2-.2" stroke="#3A2B21" stroke-width=".7"
                  fill="none" stroke-linecap="round" opacity=".75" />
            <ellipse cx="5.9" cy="8.8" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="2.9s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="10.1" cy="8.8" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="2.9s" repeatCount="indefinite" />
            </ellipse>
            <circle cx="6.5" cy="8.1" r=".5" fill="#FFFFFF" opacity=".9" />
            <circle cx="10.7" cy="8.1" r=".5" fill="#FFFFFF" opacity=".9" />
            <ellipse cx="3.5" cy="11.2" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <ellipse cx="12.5" cy="11.2" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <path d="M6.6 11.8a1.5 1.5 0 0 0 2.8 0z" fill="#C05A5A" />
          </g>
        </g>
      </svg>
    </span>
  </span>
</template>

<style scoped>
.sc { position: absolute; inset: 0; display: block; font-size: 0; }

/* 地面光斑：把白板和小人钉在地上 */
.floor {
  position: absolute; left: 50%; bottom: 4px;
  width: 34px; height: 7px; margin-left: -17px; border-radius: 50%;
  background: radial-gradient(closest-side, #1a3a662e, transparent);
}
/* 0.8px/viewBox 单位：34×30 → 27.2×24，人物头径与 online 场景一致；
   抬高 5px：站姿较矮场景与窗台小人保持同一条头部水平线 */
.bounce {
  position: absolute; left: 50%; bottom: 5px;
  display: block; filter: drop-shadow(0 1px 1px #1a3a6626);
  animation: mtSway 3.2s ease-in-out infinite alternate;
}
.bounce svg { display: block; width: 27.2px; height: 24px; }
/* 悬停时讲得更起劲 */
.sc.hot .bounce { animation-duration: 1.2s; }
@keyframes mtSway {
  from { transform: translateY(0); }
  to   { transform: translateY(-.8px); }
}
</style>
